/*
  Progress readout and mouse scrolling for the bestseller rail (sections/featured-collection.liquid).

  Publishes the list's horizontal scroll position as `--ep-rail-progress`, a 0-100 number on
  the <product-rail> element; the bar's width is derived from it in CSS. Also converts
  pointer-drag input into horizontal scroll, since this instance runs with arrows off and
  a plain desktop mouse otherwise has no way to move it. Deliberately separate from
  SliderComponent: that class owns paging, the counter and the arrow buttons; nothing here
  calls into it or touches those elements, so the two never fight over the same DOM.

  Also briefly suspends scroll-snap during native wheel/trackpad horizontal scroll, so the
  browser's mandatory snap doesn't fight trackpad momentum mid-gesture.

  A plain click has to reach the card's <a>. Capturing the pointer on pointerdown breaks that:
  the pointerup is then delivered to the list, so the browser fires the click on the list (the
  common ancestor of the pressed card and the list) and the link never activates. The pointer is
  therefore captured only once the press has moved far enough to be a drag. Until then, and for
  the rest of the gesture, pointermove/pointerup are heard on window, so a release anywhere
  still ends the drag.

  The ResizeObserver is what keeps the bar honest across breakpoints - the same list is a
  scrolling rail at one width and a static grid at another, and `scroll` never fires on that
  transition.
*/
class ProductRail extends HTMLElement {
  connectedCallback() {
    // Same lookup SliderComponent uses (assets/global.js:731), so the list can sit at any depth.
    this.slider = this.querySelector('[id^="Slider-"]');
    if (!this.slider) return;

    // One teardown for every subscription below. A section re-render in the theme editor
    // disconnects the element, and reconnecting without this would leave duplicate listeners
    // on a dead list.
    this.controller = new AbortController();
    this.slider.addEventListener('scroll', () => this.update(), { passive: true, signal: this.controller.signal });
    this.resizeObserver = new ResizeObserver(() => this.update());
    this.resizeObserver.observe(this.slider);
    this.update();

    this.slider.addEventListener('pointerdown', (event) => this.onPointerDown(event), {
      signal: this.controller.signal,
    });
    // pointermove/pointerup/pointercancel are not listened for here: onPointerDown adds them on
    // window for the length of one gesture. Only the loss of a capture needs a standing listener,
    // because the browser can drop it (menu closed, element removed) without a pointerup.
    this.slider.addEventListener('lostpointercapture', () => this.onPointerEnd(), {
      signal: this.controller.signal,
    });
    // Capture phase: needs to intercept the click before it reaches a card's <a> and navigates.
    this.slider.addEventListener('click', (event) => this.onClickCapture(event), {
      capture: true,
      signal: this.controller.signal,
    });
    // Native HTML5 drag-and-drop on a card's <a>/<img> fires independently of the pointer
    // events above and isn't stopped by setPointerCapture; left alone it drags the link/image
    // itself (ghost image) instead of - or fighting with - the pointer-drag scroll.
    this.slider.addEventListener('dragstart', (event) => event.preventDefault(), {
      signal: this.controller.signal,
    });
    // Native trackpad horizontal scroll never touches the pointer handlers above, so
    // scroll-snap stays engaged for the whole gesture and can spring the list back mid-
    // momentum. Toggle the same class the drag path uses; never touch scrollLeft here.
    this.slider.addEventListener('wheel', (event) => this.onWheel(event), {
      passive: true,
      signal: this.controller.signal,
    });
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.dragAbort?.abort();
    this.resizeObserver?.disconnect();
    clearTimeout(this.wheelSettleTimer);
  }

  onPointerDown(event) {
    // Touch keeps its native swipe untouched. Primary button only, so a middle-click or a
    // right-click on a card keeps its usual meaning.
    if (event.pointerType !== 'mouse' || event.button !== 0) return;
    this.dragging = true;
    this.dragged = false;
    this.captured = false;
    this.dragStartX = event.clientX;
    this.dragStartScrollLeft = this.slider.scrollLeft;
    this.dragPointerId = event.pointerId;
    this.slider.classList.add('is-interacting', 'is-dragging');

    // No setPointerCapture here: see the note at the top of the file. Listen on window for the
    // length of this one gesture instead, so the drag follows the pointer out of the list and a
    // release anywhere (even outside the rail, or outside the window) ends it.
    this.dragAbort?.abort();
    this.dragAbort = new AbortController();
    const { signal } = this.dragAbort;
    window.addEventListener('pointermove', (moveEvent) => this.onPointerMove(moveEvent), { signal });
    window.addEventListener('pointerup', () => this.onPointerEnd(), { signal });
    window.addEventListener('pointercancel', () => this.onPointerEnd(), { signal });
  }

  onPointerMove(event) {
    if (!this.dragging) return;
    // The button came up where no pointerup was heard: stop instead of scrolling on hover.
    if (event.buttons === 0) {
      this.onPointerEnd();
      return;
    }
    const delta = event.clientX - this.dragStartX;
    if (!this.dragged) {
      if (Math.abs(delta) <= 6) return; // Still a click: leave the card's <a> alone.
      this.dragged = true;
      // Now it is a drag. Capture so the list keeps receiving the pointer (no hover on whatever
      // it passes over, no text selection) and so the release, and the click that follows it,
      // land on the list and not on a card. onClickCapture swallows that click.
      try {
        this.slider.setPointerCapture(this.dragPointerId);
        this.captured = true;
      } catch (error) {
        // The pointer is no longer active (released between events): there is nothing to drag.
        this.onPointerEnd();
        return;
      }
    }
    this.slider.scrollLeft = this.dragStartScrollLeft - delta;
  }

  onPointerEnd() {
    if (!this.dragging) return; // Also what stops lostpointercapture re-entering from here.
    this.dragging = false;
    this.dragAbort?.abort();
    this.slider.classList.remove('is-interacting', 'is-dragging');
    if (this.captured) {
      this.captured = false;
      try {
        this.slider.releasePointerCapture(this.dragPointerId);
      } catch (error) {
        // Already released with the pointer.
      }
    }
    // The click that ends a drag is dispatched right after this pointerup, and onClickCapture
    // clears the flag when it swallows it. If no click arrives (or it lands outside the list),
    // clear it here once that moment has passed, so it can never swallow a later click, for
    // instance one from the keyboard.
    if (this.dragged) setTimeout(() => (this.dragged = false), 0);
  }

  onWheel(event) {
    if (event.deltaX === 0) return; // Vertical-only scroll: leave scroll-snap engaged.
    clearTimeout(this.wheelSettleTimer);
    this.slider.classList.add('is-interacting');
    this.wheelSettleTimer = setTimeout(() => this.slider.classList.remove('is-interacting'), 150);
  }

  onClickCapture(event) {
    // Suppresses the click that follows a drag release so it doesn't also open the card's link.
    if (!this.dragged) return;
    event.preventDefault();
    event.stopPropagation();
    this.dragged = false;
  }

  update() {
    const max = this.slider.scrollWidth - this.slider.clientWidth;
    // A non-scrolling grid reports max <= 0; report 0 rather than dividing by it.
    const percent = max > 0 ? Math.round((this.slider.scrollLeft / max) * 100) : 0;
    this.style.setProperty('--ep-rail-progress', percent);
  }
}

if (!customElements.get('product-rail')) customElements.define('product-rail', ProductRail);
